import polyglotI18nProvider from 'ra-i18n-polyglot';
import { MsalProvider, AuthenticatedTemplate, useMsal, UnauthenticatedTemplate } from '@azure/msal-react';
import { Admin, Resource, localStorageStore, useStore, StoreContextProvider } from 'react-admin';

import authProvider from './authProvider';
import { Dashboard } from './dashboard';
import dataProviderFactory from './dataProvider';
import englishMessages from './i18n/en';
import { Layout, Login } from './layout';
import { themes, ThemeName } from './themes/themes';
import report from './claims-report';
import fees from './msa-fees';
import Reports from './reports/reports';
import HistoricalReports from './historical-reports/HistoricalReports';
import BillingClients from './billing-clients/BillingClients';
import MemberSummary from './member-summary/MemberSummary';

import pharmacyList from './filters/pharmacy/PharmacyList';
import clientList from './filters/clients/ClientList';
import MedicaidClaimUpload from './medicaidUpload/MedicaidClaimUpload';

const i18nProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === 'fr') {
      return import('./i18n/fr').then((messages) => messages.default);
    }

    // Always fallback on english
    return englishMessages;
  },
  'en',
  [
    { locale: 'en', name: 'English' },
    { locale: 'fr', name: 'Français' },
  ]
);

const store = localStorageStore(undefined, 'truerx');

const App = () => {
  const [themeName] = useStore<ThemeName>('themeName', 'nano');
  const lightTheme = themes.find((theme) => theme.name === themeName)?.light;
  const darkTheme = themes.find((theme) => theme.name === themeName)?.dark;
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();

  //console.log('Active Account ', activeAccount)
  let dataprovider = dataProviderFactory(process.env.REACT_APP_DATA_PROVIDER || 'graphql');

  return (
    <>
      <AuthenticatedTemplate>
        {activeAccount ? (
          <Admin
            title=""
            dataProvider={dataprovider}
            store={store}
            authProvider={authProvider(activeAccount, instance)}
            dashboard={Dashboard}
            loginPage={Login}
            layout={Layout}
            i18nProvider={i18nProvider}
            disableTelemetry
            lightTheme={lightTheme}
            darkTheme={darkTheme}
            defaultTheme="light"
          >
            <Resource name="claimsreport" {...report} />
            <Resource name="fees" {...fees} />
            <Resource name="reports" {...Reports} />
            <Resource name="HistoricalReports" {...HistoricalReports} />
            <Resource name="BillingClients" {...BillingClients} />
            <Resource name="MemberSummary" {...MemberSummary} />
            <Resource name="MedicaidClaimUpload" {...MedicaidClaimUpload} />
          </Admin>
        ) : null}
        {/* <CustomFooter/>*/}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Admin
          title=""
          dataProvider={dataProviderFactory(process.env.REACT_APP_DATA_PROVIDER || '')}
          store={store}
          authProvider={authProvider(activeAccount, instance)}
          dashboard={Dashboard}
          loginPage={Login}
          layout={Layout}
          i18nProvider={i18nProvider}
          disableTelemetry
          lightTheme={lightTheme}
          darkTheme={darkTheme}
          defaultTheme="light"
        >
          <Resource name="claimreports" {...report} />
        </Admin>
      </UnauthenticatedTemplate>
    </>
  );
};

// @ts-ignore
const AppWrapper = ({ instance }) => (
  <MsalProvider instance={instance}>
    <StoreContextProvider value={store}>
      <App />
    </StoreContextProvider>
  </MsalProvider>
);

export default AppWrapper;
